import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue } from "framer-motion";
import {
  FaArrowRight,
  FaBars,
  FaBolt,
  FaBuilding,
  FaChevronRight,
  FaCircleCheck,
  FaDroplet,
  FaFan,
  FaHammer,
  FaHouse,
  FaPaintRoller,
  FaShieldHalved,
  FaScrewdriverWrench,
  FaStar,
  FaWarehouse,
  FaWrench,
  FaXmark,
} from "react-icons/fa6";
import logo from "./assets/htx-logo.png";
import { EstimateModal } from "./EstimateModal";
import appHomeShot from "./assets/app-home-shot.PNG";
import appEstimateShot from "./assets/app-estimate-shot.PNG";
import { FaApple } from "react-icons/fa6";
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  },
};

const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const pageTransition = {
  initial: { opacity: 0, y: 16 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.28, ease: [0.4, 0, 1, 1] },
  },
};

function Glow({ className = "" }) {
  return <div className={`pointer-events-none absolute rounded-full blur-3xl ${className}`} />;
}

function SectionEyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-white/70 backdrop-blur-xl">
      {children}
    </div>
  );
}

function ScrollReveal({
  children,
  className = "",
  delay = 0,
  y = 40,
  duration = 0.8,
  amount = 0.2,
}) {
  return (
    <motion.section
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount }}
      transition={{
        duration,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      {children}
    </motion.section>
  );
}


function GlassCard({ children, className = "" }) {
  return (
    <div
      className={`rounded-[30px] border border-white/10 bg-white/[0.04] shadow-[0_24px_80px_rgba(0,0,0,0.28)] backdrop-blur-2xl ${className}`}
    >
      {children}
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-3 text-sm leading-relaxed text-white/78">
      <FaCircleCheck className="mt-0.5 shrink-0 text-[#ef4444]" />
      <span>{children}</span>
    </div>
  );
}

function ServicePill({ children }) {
  return (
    <div className="rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-sm text-white/80 backdrop-blur-xl">
      {children}
    </div>
  );
}

function ServiceCard({ service, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`group relative overflow-hidden rounded-[28px] border p-6 text-left transition-all duration-300 ${
        active
          ? "border-[#ef4444]/45 bg-white/[0.08] shadow-[0_18px_45px_rgba(239,68,68,0.16)]"
          : "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.06]"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-2xl border text-xl transition ${
            active
              ? "border-[#ef4444]/35 bg-[#ef4444]/15 text-[#ff6666]"
              : "border-white/10 bg-white/[0.05] text-white/80"
          }`}
        >
          {service.icon}
        </div>

        <div
          className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.18em] ${
            active ? "bg-[#ef4444] text-white" : "bg-white/[0.05] text-white/50"
          }`}
        >
          {service.badge}
        </div>
      </div>

      <h3 className="mt-5 text-xl font-semibold tracking-[-0.02em] text-white">
        {service.title}
      </h3>

      <p className="mt-3 text-sm leading-relaxed text-white/65">{service.summary}</p>

      <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white/82">
        View details
        <FaChevronRight className="transition-transform duration-300 group-hover:translate-x-1" />
      </div>
    </button>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <GlassCard className="p-7">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#ef4444]/25 bg-[#ef4444]/10 text-xl text-[#ff6767]">
        {icon}
      </div>
      <h3 className="mt-5 text-xl font-semibold text-white">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-white/68">{body}</p>
    </GlassCard>
  );
}

function ReviewCard({ quote, author }) {
  return (
    <GlassCard className="h-full p-6">
      <div className="flex gap-1 text-[#ef4444]">
        <FaStar />
        <FaStar />
        <FaStar />
        <FaStar />
        <FaStar />
      </div>
      <p className="mt-5 text-base leading-relaxed text-white/82">“{quote}”</p>
      <p className="mt-5 text-sm font-semibold text-white/70">{author}</p>
    </GlassCard>
  );
}

function FaqItem({ item, open, onToggle }) {
  return (
    <GlassCard className="overflow-hidden">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="text-base font-semibold text-white">{item.q}</span>
        <span className="text-white/70">{open ? <FaXmark /> : <FaChevronRight />}</span>
      </button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24 }}
          >
            <div className="px-6 pb-6 pt-0 text-sm leading-relaxed text-white/68">{item.a}</div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </GlassCard>
  );
}

export default function App() {
  const [openEstimate, setOpenEstimate] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [page, setPage] = useState("home");
  const [activeService, setActiveService] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
const [isPaused, setIsPaused] = useState(false);

const marqueeX = useMotionValue(0);
const marqueeTrackRef = useRef(null);
const marqueeWrapRef = useRef(null);


  const pageTopRef = useRef(null);

  useEffect(() => {
    if (pageTopRef.current) {
      pageTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [page]);
  

  useEffect(() => {
    if (!mobileMenu) return;
    const onResize = () => {
      if (window.innerWidth >= 768) setMobileMenu(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [mobileMenu]);
useEffect(() => {
  if (page !== "services") return;

  let frameId;
  let lastTime = 0;
  const speed = 46; // pixels per second

  const animate = (time) => {
    if (!lastTime) lastTime = time;
    const delta = (time - lastTime) / 1000;
    lastTime = time;

    const track = marqueeTrackRef.current;
    const wrap = marqueeWrapRef.current;

    if (track && wrap && !isPaused) {
      const singleSetWidth = track.scrollWidth / 2;
      let nextX = marqueeX.get() - speed * delta;

      if (Math.abs(nextX) >= singleSetWidth) {
        nextX += singleSetWidth;
      }

      marqueeX.set(nextX);
    }

    frameId = requestAnimationFrame(animate);
  };

  frameId = requestAnimationFrame(animate);

  return () => cancelAnimationFrame(frameId);
}, [page, isPaused, marqueeX]);
  
  const services = useMemo(
    () => [
      {
        title: "General Construction",
        badge: "Build",
        icon: <FaHammer />,
        summary:
          "For larger renovation, repair, and improvement work that needs a capable contractor coordinating the whole scope.",
        description:
          "Whether it is a major repair, rebuild, renovation, or structural improvement, this is the category homeowners look for when they need one contractor to handle the project professionally from start to finish.",
        bullets: [
          "Structural repair and reinforcement",
          "Concrete and slab work",
          "Framing and rebuild work",
          "General repair and renovation scopes",
          "Waterproofing and weather-related repairs",
          "Residential and commercial improvement work",
        ],
      },
      {
        title: "Exterior Services",
        badge: "Outside",
        icon: <FaHouse />,
        summary:
          "The full outside of the property, including repairs, upgrades, improvements, and curb-appeal work.",
        description:
          "This covers the parts of the home people see first and the issues that often become urgent first: fencing, driveway work, exterior finishes, outdoor projects, and property upgrades.",
        bullets: [
          "Fence installation and repair",
          "Driveway, paver, and hardscape work",
          "Stucco and exterior finish repair",
          "Exterior painting and trim",
          "Pergolas, gazebos, and outdoor projects",
          "General exterior property improvements",
        ],
      },
      {
        title: "Interior Remodeling",
        badge: "Inside",
        icon: <FaPaintRoller />,
        summary:
          "Home updates and remodeling work designed to improve function, finish quality, and overall look.",
        description:
          "This is for kitchens, bathrooms, flooring, drywall, tile, and interior upgrades where homeowners want the job done cleanly and the result to feel finished, not patched together.",
        bullets: [
          "Kitchen and bathroom updates",
          "Drywall, paint, and trim work",
          "Flooring and tile installation",
          "Cabinet and finish work",
          "Ceiling and wall repairs",
          "Full-room and full-home remodeling",
        ],
      },
      {
        title: "Electrical & HVAC",
        badge: "Systems",
        icon: <FaBolt />,
        summary:
          "Core home systems handled under one roof so customers do not need to hunt down separate specialty crews.",
        description:
          "When the issue is power, comfort, airflow, wiring, or a system upgrade, this category gives the company the credibility to handle real home-service demand instead of looking like a one-trade shop.",
        bullets: [
          "Electrical troubleshooting and upgrades",
          "Panels, lighting, and fixture installation",
          "Ceiling fans and dedicated circuits",
          "HVAC repair and maintenance",
          "System replacement coordination",
          "Home comfort and efficiency upgrades",
        ],
      },
      {
        title: "Plumbing Services",
        badge: "Water",
        icon: <FaDroplet />,
        summary:
          "The everyday service category homeowners need constantly, from urgent issues to routine replacements.",
        description:
          "Leaks, clogged lines, fixtures, water issues, and plumbing repairs are some of the highest-intent service calls a home service company can get. This section should feel clear, responsive, and dependable.",
        bullets: [
          "Leak detection and repair",
          "Drain cleaning and line issues",
          "Pipe repair and replacement",
          "Fixture installation and replacement",
          "Toilet, faucet, and shower work",
          "General residential plumbing service",
        ],
      },
      {
        title: "Commercial Services",
        badge: "Commercial",
        icon: <FaBuilding />,
        summary:
          "A stronger offering for offices, retail spaces, investor projects, and property improvement work.",
        description:
          "Commercial capability makes the company look larger, more trusted, and more established. It also supports property managers, investors, and business owners who need fast, professional execution.",
        bullets: [
          "Office and retail improvement work",
          "Tenant improvements",
          "General commercial repair and renovation",
          "Restaurant and business property updates",
          "Make-ready and turnover scopes",
          "Property improvement coordination",
        ],
      },
      {
        title: "Handyman & Home Repair",
        badge: "Everyday",
        icon: <FaScrewdriverWrench />,
        summary:
          "Small jobs, punch lists, and everyday repairs that make the company the first call, not just the call for big jobs.",
        description:
          "This is what turns the business into a true full-service home company. It catches all the smaller work homeowners still need done but often do not know who to call for.",
        bullets: [
          "General repair and maintenance",
          "Door, trim, and hardware fixes",
          "Punch-list items",
          "Installations and replacements",
          "Rental turn and make-ready items",
          "Odd jobs and small improvement work",
        ],
      },
      {
        title: "Roofing & Weather Damage",
        badge: "Roof",
        icon: <FaWrench />,
        summary:
          "Roof-related repair and protection work for one of the most important parts of the home.",
        description:
          "Roof work is one of the highest-trust categories in home services. This section keeps the company competitive for inspections, repairs, storm damage, and protection-focused work.",
        bullets: [
          "Roof repair",
          "Storm and weather damage response",
          "Leak source investigation",
          "Roof maintenance coordination",
          "Protective exterior repair work",
          "Insurance-related repair communication",
        ],
      },
    ],
    []
  );

  const reviews = useMemo(
    () => [
      {
        quote:
          "Very professional, responsive, and easy to work with. They handled multiple items at our home and made the process simple.",
        author: "Residential client",
      },
      {
        quote:
          "It is hard to find one company that can actually coordinate different types of work well. This team made that part easy.",
        author: "Home improvement customer",
      },
      {
        quote:
          "Fast communication, clean work, and a much better experience than dealing with multiple separate contractors.",
        author: "Property owner",
      },
    ],
    []
  );

const faqs = useMemo(
  () => [
    {
      q: "What kinds of jobs can HTX Home Solutions handle?",
      a: "The company is positioned to handle a wide range of home and property needs, including general construction, repairs, remodeling, plumbing, electrical and HVAC work, exterior improvements, roofing-related repair work, handyman services, and commercial improvement projects.",
    },
    {
      q: "Do you only handle large projects?",
      a: "No. The site is built to support both larger renovation or repair scopes and smaller everyday home-service calls. That includes punch-list items, repair visits, replacements, and general maintenance work.",
    },
    {
      q: "Can I request service even if I am not sure which category my project fits?",
      a: "Yes. The estimate flow should feel simple for the homeowner. They do not need to know the perfect category before reaching out. The goal is to make it easy to explain the issue and get the conversation started.",
    },
    {
      q: "Do you serve both homeowners and commercial clients?",
      a: "Yes. The service structure supports residential homeowners, investors, landlords, property managers, and business owners who need improvement or repair work completed professionally.",
    },

    // NEW ONES ↓↓↓

    {
      q: "How quickly can you start a project or schedule service?",
      a: "Response times depend on the type and urgency of the project, but most service requests are reviewed quickly and scheduled as soon as availability allows. Urgent repair needs can often be prioritized.",
    },
    {
      q: "Do you handle multiple services in one project?",
      a: "Yes. Many projects involve multiple trades, and the company is structured to coordinate everything under one team. This reduces delays, miscommunication, and the need to hire multiple contractors.",
    },
    {
      q: "Are estimates free?",
      a: "Yes. You can request a free estimate by submitting your project details. The goal is to provide clear next steps and pricing guidance without unnecessary friction.",
    },
    {
      q: "Do you work with investment properties or rental units?",
      a: "Yes. Services are available for rental properties, investor portfolios, make-ready work, and ongoing property maintenance needs.",
    },
    {
      q: "What areas do you service?",
      a: "Service coverage is based in the local market and surrounding areas. If you are unsure whether your location is covered, you can submit a request and the team will confirm availability.",
    },
  ],
  []
);

  
  const currentService = services[activeService];

 const nav = [
  { key: "home", label: "Home" },
  { key: "services", label: "Services" },
  { key: "projects", label: "Projects" },
  { key: "faq", label: "FAQ" },
  { key: "privacy", label: "Privacy" },
  { key: "terms", label: "Terms" },
];

  const renderPage = () => {

    if (page === "privacy") {
  return (
    <div className="max-w-3xl mx-auto text-white space-y-6">
      <h1 className="text-3xl font-bold">Privacy Policy</h1>

      <p>
        HTX Home Solutions ("we", "our", or "us") respects your privacy and is committed
        to protecting your information.
      </p>

      <h2 className="text-xl font-semibold">Information We Collect</h2>
      <p>
        We may collect personal information including your name, phone number, email,
        address, and details about your service request.
      </p>

      <h2 className="text-xl font-semibold">How We Use Information</h2>
      <p>
        We use your information to:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Provide and manage services</li>
        <li>Communicate regarding requests or updates</li>
        <li>Improve our services</li>
      </ul>

      <h2 className="text-xl font-semibold">SMS Communication</h2>
      <p>
        By providing your phone number, you consent to receive SMS messages related to
        your service request. Message frequency varies. Message and data rates may apply.
        You can reply STOP to opt out at any time.
      </p>

      <h2 className="text-xl font-semibold">Data Sharing</h2>
      <p>
        We do not sell your personal information. We may share information with service
        providers or contractors as needed to fulfill your request.
      </p>

      <h2 className="text-xl font-semibold">Security</h2>
      <p>
        We take reasonable measures to protect your information but cannot guarantee
        absolute security.
      </p>

      <h2 className="text-xl font-semibold">Contact</h2>
      <p>
        For questions, contact us at support@htxhomesolutions.com
      </p>
    </div>
  );
}
if (page === "terms") {
  return (
    <div className="max-w-3xl mx-auto text-white space-y-6">
      <h1 className="text-3xl font-bold">Terms of Service</h1>

      <p>
        By using HTX Home Solutions, you agree to the following terms.
      </p>

      <h2 className="text-xl font-semibold">Services</h2>
      <p>
        HTX Home Solutions provides home service coordination, estimates, and project
        assistance. Final agreements may be made with contractors or service providers.
      </p>

      <h2 className="text-xl font-semibold">User Responsibilities</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Provide accurate information</li>
        <li>Use services lawfully</li>
        <li>Do not misuse the platform</li>
      </ul>

      <h2 className="text-xl font-semibold">Estimates & Pricing</h2>
      <p>
        Estimates are not guaranteed final pricing and may vary depending on scope,
        materials, and labor.
      </p>

      <h2 className="text-xl font-semibold">Limitation of Liability</h2>
      <p>
        HTX Home Solutions is not liable for indirect or incidental damages related to
        services provided.
      </p>

      <h2 className="text-xl font-semibold">Changes</h2>
      <p>
        We may update these terms at any time without notice.
      </p>

      <h2 className="text-xl font-semibold">Contact</h2>
      <p>
        support@htxhomesolutions.com
      </p>
    </div>
  );
}
    if (page === "services") {
  return (
    <motion.div
      key="services"
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-16"
    >
      <section className="text-center">
        <SectionEyebrow>
          <FaShieldHalved className="text-[#ef4444]" />
          Full-Service Contractor
        </SectionEyebrow>

        <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
          Complete home services,
          <span className="block text-white/60">organized the right way.</span>
        </h2>

        <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-white/68">
          From repairs and remodeling to plumbing, electrical, exterior work, and
          commercial improvement projects, we bring everything together under one team.
        </p>
      </section>

      <section className="mx-auto max-w-6xl">
        <GlassCard className="mx-auto max-w-4xl overflow-hidden p-8 md:p-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentService.title}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[#ef4444]/30 bg-[#ef4444]/12 text-3xl text-[#ff6666]">
                  {currentService.icon}
                </div>

                <div className="mt-4 rounded-full bg-white/[0.05] px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-white/55">
                  {currentService.badge}
                </div>

                <h3 className="mt-6 text-3xl font-semibold tracking-[-0.03em] text-white md:text-4xl">
                  {currentService.title}
                </h3>

                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/68 md:text-base">
                  {currentService.description}
                </p>
              </div>

              <div className="mx-auto mt-8 grid max-w-3xl gap-4 md:grid-cols-2">
                {currentService.bullets.map((item) => (
                  <Bullet key={item}>{item}</Bullet>
                ))}
              </div>

              <div className="mt-10 flex justify-center">
                <button
                  onClick={() => setOpenEstimate(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#ef4444] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(239,68,68,0.35)] transition hover:scale-[1.02]"
                >
                  Request Free Estimate
                  <FaArrowRight />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </GlassCard>
      </section>

      <section className="mx-auto max-w-7xl overflow-hidden">
  <div className="mb-5 flex items-center justify-between gap-4">
    <h3 className="text-xl font-semibold text-white md:text-2xl">
      Browse Services
    </h3>

    <div className="text-sm text-white/45">
      Select any category to view details
    </div>
  </div>

  <div className="relative overflow-hidden">
    <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-[#06080f] to-transparent" />
    <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-[#06080f] to-transparent" />

<div
  ref={marqueeWrapRef}
  className="overflow-hidden"
  onMouseEnter={() => setIsPaused(true)}
  onMouseLeave={() => setIsPaused(false)}
>
  <motion.div
    ref={marqueeTrackRef}
    className="flex w-max gap-4 pb-4"
    style={{ x: marqueeX }}
  >
      {[...services, ...services].map((service, index) => {
        const realIndex = index % services.length;
        const isActive = activeService === realIndex;

        return (
          <button
            key={`${service.title}-${index}`}
            onClick={() => setActiveService(realIndex)}
            className={`min-w-[280px] max-w-[280px] rounded-[28px] border p-6 text-left transition-all duration-300 md:min-w-[320px] md:max-w-[320px] ${
              isActive
                ? "border-[#ef4444]/45 bg-white/[0.08] shadow-[0_18px_45px_rgba(239,68,68,0.16)]"
                : "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.06]"
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-2xl border text-xl transition ${
                  isActive
                    ? "border-[#ef4444]/35 bg-[#ef4444]/15 text-[#ff6666]"
                    : "border-white/10 bg-white/[0.05] text-white/80"
                }`}
              >
                {service.icon}
              </div>

              <div
                className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.18em] ${
                  isActive
                    ? "bg-[#ef4444] text-white"
                    : "bg-white/[0.05] text-white/50"
                }`}
              >
                {service.badge}
              </div>
            </div>

            <h4 className="mt-5 text-xl font-semibold tracking-[-0.02em] text-white">
              {service.title}
            </h4>

            <p className="mt-3 text-sm leading-relaxed text-white/65">
              {service.summary}
            </p>

            <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white/82">
              View details
              <FaArrowRight className="transition-transform duration-300" />
            </div>
          </button>
        );
      })}
    </motion.div>
  </div>
  </div>
</section>
    </motion.div>
  );
}

    if (page === "projects") {
      return (
        <motion.div
          key="projects"
          variants={pageTransition}
          initial="initial"
          animate="animate"
          exit="exit"
          className="space-y-16"
        >
          <section className="text-center">
            <SectionEyebrow>Project Focus</SectionEyebrow>
            <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
              Built for the jobs
              <span className="block text-white/60">customers actually hire for.</span>
            </h2>
           <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-white/68">
  We support everything from everyday repairs to larger residential and commercial improvement projects.
</p>
          </section>

          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="grid gap-5 md:grid-cols-2 xl:grid-cols-3"
          >
            {[
              {
                title: "Whole-Home Repair & Improvement",
                body:
                  "A full-scope residential project where multiple trades and repairs need to be handled under one roof with clear communication and clean execution.",
                icon: <FaHouse />,
              },
              {
                title: "Kitchen, Bath, and Interior Upgrades",
                body:
                  "For homeowners improving finish quality, layout, or function through remodeling, fixture replacement, flooring, paint, tile, and interior work.",
                icon: <FaPaintRoller />,
              },
              {
                title: "Commercial Property Improvements",
                body:
                  "For offices, storefronts, investor properties, and business locations that need professional repair, renovation, or make-ready services.",
                icon: <FaWarehouse />,
              },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp}>
                <GlassCard className="h-full p-7">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#ef4444]/25 bg-[#ef4444]/10 text-xl text-[#ff6666]">
                    {item.icon}
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-white">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/68">{item.body}</p>
                </GlassCard>
              </motion.div>
            ))}
          </motion.div>

         <GlassCard className="p-8">
  <div className="grid items-start gap-8 lg:grid-cols-2">
    
    <div>
      <SectionEyebrow>Why homeowners respond</SectionEyebrow>

      <h3 className="mt-5 text-3xl font-semibold tracking-[-0.03em] text-white">
        One company.
        <span className="block text-white/60">Less hassle.</span>
      </h3>

      <p className="mt-4 text-sm leading-relaxed text-white/68">
        Homeowners shouldn't have to coordinate five different contractors just
        to complete one project. HTX Home Solutions is built to simplify that
        process by providing one reliable team capable of handling the majority
        of residential and property improvement work.
      </p>
    </div>

    <div className="flex flex-col justify-center space-y-4 lg:max-w-xl lg:mx-auto">
  <Bullet>Broad service coverage without overwhelming the customer</Bullet>
  <Bullet>Clear quote request options throughout the site</Bullet>
  <Bullet>Residential and commercial project capability</Bullet>
  <Bullet>Simple explanations of services and work types</Bullet>
  <Bullet>Clean modern design that still feels like a real contractor company</Bullet>
</div>

  </div>
</GlassCard>
        </motion.div>
      );
    }

    if (page === "faq") {
      return (
        <motion.div
          key="faq"
          variants={pageTransition}
          initial="initial"
          animate="animate"
          exit="exit"
          className="space-y-16"
        >
          <section className="text-center">
            <SectionEyebrow>Frequently Asked Questions</SectionEyebrow>
            <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
              Questions homeowners ask
              <span className="block text-white/60">before they hire.</span>
            </h2>
          </section>

          <div className="mx-auto grid max-w-4xl gap-4">
            {faqs.map((item, index) => (
              <FaqItem
                key={item.q}
                item={item}
                open={openFaq === index}
                onToggle={() => setOpenFaq(openFaq === index ? -1 : index)}
              />
            ))}
          </div>

          <GlassCard className="mx-auto max-w-4xl p-8 text-center">
            <h3 className="text-2xl font-semibold text-white">
              Ready to get pricing on your project?
            </h3>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/68">
              Tell us what you need done and we’ll take it from there.
            </p>
            <button
              onClick={() => setOpenEstimate(true)}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#ef4444] px-8 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(239,68,68,0.35)] transition hover:scale-[1.02]"
            >
              Get Free Estimate
              <FaArrowRight />
            </button>
          </GlassCard>
        </motion.div>
      );
    }

return (
  <motion.div
    key="home"
    variants={pageTransition}
    initial="initial"
    animate="animate"
    exit="exit"
    className="space-y-20"
  >
    <section className="relative grid items-center gap-14 lg:grid-cols-[0.95fr_1.05fr]">
  <Glow className="left-[-100px] top-[-50px] h-[300px] w-[300px] bg-[#ef4444]/18" />
  <Glow className="right-[-60px] top-[80px] h-[280px] w-[280px] bg-white/10" />

  <motion.div
    variants={fadeUp}
    initial="hidden"
    animate="show"
    className="relative z-10"
  >
    <SectionEyebrow>
      <FaApple className="text-[#ef4444]" />
      iPhone App Available
    </SectionEyebrow>

    <h1 className="mt-7 max-w-4xl text-5xl font-semibold leading-[0.95] tracking-[-0.05em] text-white sm:text-6xl lg:text-7xl">
      Book projects, review estimates, and manage everything
      <span className="block text-white/60">from one clean app.</span>
    </h1>

    <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
      HTX Home Solutions makes it easy to request service, track projects,
      approve estimates, and stay connected from your phone.
    </p>

    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
      <a
        href="https://apps.apple.com/us/app/YOUR-APP-ID-HERE"
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ef4444] px-8 py-4 text-sm font-semibold text-white shadow-[0_22px_55px_rgba(239,68,68,0.38)] transition duration-300 hover:scale-[1.02]"
      >
        <FaApple className="text-lg" />
        Download on iOS
      </a>

      <button
        onClick={() => setOpenEstimate(true)}
        className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-8 py-4 text-sm font-semibold text-white/90 backdrop-blur-xl transition hover:bg-white/[0.08]"
      >
        Request Free Estimate
        <FaArrowRight />
      </button>
    </div>

    {/* <div className="mt-8 flex flex-wrap gap-3">
      <ServicePill>Request service fast</ServicePill>
      <ServicePill>Approve estimates</ServicePill>
      <ServicePill>Track project updates</ServicePill>
    </div> */}
  </motion.div>

  <motion.div
    variants={fadeUp}
    initial="hidden"
    animate="show"
    className="relative flex items-center justify-center"
  >
    <div className="relative flex w-full max-w-[640px] items-center justify-center">
      <div className="absolute inset-0 rounded-[40px] bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.18),transparent_65%)] blur-2xl" />

      <div className="relative h-[640px] w-full max-w-[520px]">

         <div className="absolute inset-0 flex items-center justify-center">
    <div className="h-[420px] w-[420px] rounded-full bg-[#ef4444]/10 blur-[120px]" />
  </div>
  
<div className="absolute left-[40px] md:left-[40px] top-12  z-10 hidden w-[240px] md:w-[270px] rotate-[-4deg] rounded-[42px] border border-white/10 bg-black p-2.5 shadow-[0_30px_90px_rgba(0,0,0,0.55)] md:block"> <div className="rounded-[34px] bg-[#0a0a0a] p-1">
            <img
              src={appEstimateShot}
              alt="HTX Home Solutions estimate approval screen"
              className="h-auto w-full rounded-[24px] object-cover"
            />
          </div>
        </div>

<div className="absolute right-[10px] md:right-[10px] top-6 md:top-8 top-0 z-20 w-[240px] rotate-[4deg] rounded-[42px] border border-white/10 bg-black p-2.5 shadow-[0_35px_120px_rgba(0,0,0,0.65)] sm:w-[270px] md:w-[300px]">

<div className="rounded-[34px] bg-[#0a0a0a] p-1">
            <img
              src={appHomeShot}
              alt="HTX Home Solutions home screen"
              className="h-auto w-full rounded-[28px] object-cover"
            />
            
          </div>
        </div>

        {/* <div className="absolute bottom-0 left-1/2 z-30 w-full max-w-[360px] -translate-x-1/2 rounded-[30px] border border-white/10 bg-white/[0.05] p-5 text-left shadow-[0_20px_60px_rgba(0,0,0,0.38)] backdrop-blur-2xl">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/45">
            In the app
          </p>
          <h3 className="mt-3 text-xl font-semibold text-white">
            Everything in one place
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-white/68">
            Browse services, approve deposits, and keep projects moving without
            chasing texts or calls.
          </p>
        </div> */}
      </div>
    </div>
  </motion.div>
</section>

    <ScrollReveal className="grid gap-4 md:grid-cols-3" delay={0.05}>
      <FeatureCard
        icon={<FaWrench />}
        title="Comprehensive home services"
        body="One company for repairs, remodeling, exterior work, plumbing, electrical and HVAC, roofing-related repair, handyman work, and commercial improvements."
      />
      <FeatureCard
        icon={<FaFan />}
        title="Responsive project support"
        body="From single repair calls to larger property scopes, we help keep projects moving with clear communication and dependable service."
      />
      <FeatureCard
        icon={<FaShieldHalved />}
        title="Professional from first contact"
        body="Clear communication, reliable service, and a straightforward estimate process from the first call."
      />
    </ScrollReveal>

    <ScrollReveal className="grid gap-6 lg:grid-cols-[0.96fr_1.04fr]" delay={0.08}>
      <GlassCard className="p-8">
        <SectionEyebrow>What we do</SectionEyebrow>
        <h2 className="mt-5 text-3xl font-semibold tracking-[-0.03em] text-white">
          One company for the
          <span className="block text-white/60">full scope of home service work.</span>
        </h2>

        <div className="mt-6 space-y-4">
          <Bullet>General construction, repairs, and improvement work</Bullet>
          <Bullet>Interior remodeling and finish updates</Bullet>
          <Bullet>Exterior, hardscape, and curb-appeal work</Bullet>
          <Bullet>Plumbing, electrical, and HVAC service categories</Bullet>
          <Bullet>Roofing-related repair and weather-damage support</Bullet>
          <Bullet>Commercial, investor, and make-ready improvement scopes</Bullet>
        </div>

        <div className="mt-8">
          <button
            onClick={() => setPage("services")}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-6 py-3.5 text-sm font-semibold text-white/90"
          >
            Explore all services
            <FaArrowRight />
          </button>
        </div>
      </GlassCard>

     <GlassCard className="p-8">
  <SectionEyebrow>Why homeowners choose us</SectionEyebrow>

  <h2 className="mt-5 text-3xl font-semibold tracking-[-0.03em] text-white">
    One company.
    <span className="block text-white/60">Less hassle.</span>
  </h2>

  <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/68">
    Homeowners should not have to coordinate multiple companies just to complete
    one project. We simplify the process by handling a wide range of services
    under one reliable team.
  </p>

  <div className="mt-6 space-y-4">
    <Bullet>Broad service coverage for real-world property needs</Bullet>
    <Bullet>Clear estimate request options throughout the process</Bullet>
    <Bullet>Residential and commercial project capability</Bullet>
    <Bullet>Straightforward service explanations</Bullet>
    <Bullet>Reliable communication and consistent execution</Bullet>
  </div>
</GlassCard>
    </ScrollReveal>

    <ScrollReveal className="mt-4" delay={0.1}>
      <div className="text-center">
        <SectionEyebrow>Popular Services</SectionEyebrow>
        <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
          Solutions for the work
          <span className="block text-white/60">property owners need most.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-white/68">
          Reliable coverage across repair work, remodeling, home systems,
          exterior improvements, roofing-related issues, and commercial jobs.
        </p>
      </div>

      <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {services.map((service, index) => (
          <ServiceCard
            key={service.title}
            service={service}
            active={activeService === index}
            onClick={() => {
              setActiveService(index);
              setPage("services");
            }}
          />
        ))}
      </div>
    </ScrollReveal>

    <ScrollReveal className="mt-4" delay={0.12}>
      <div className="text-center">
        <SectionEyebrow>Customer Reviews</SectionEyebrow>
        <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
          Service that property owners
          <span className="block text-white/60">can feel confident hiring.</span>
        </h2>
      </div>

      <div className="mt-10 grid gap-4 md:grid-cols-3">
        {reviews.map((review) => (
          <ReviewCard key={review.quote} {...review} />
        ))}
      </div>
    </ScrollReveal>

    <ScrollReveal className="mt-4" delay={0.14}>
      <GlassCard className="overflow-hidden p-8 md:p-10">
        <div className="grid items-center gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <SectionEyebrow>Ready to get started?</SectionEyebrow>
            <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-white md:text-5xl">
              Tell us what needs to be done.
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/68">
              Whether it is a repair, remodel, urgent issue, or larger
              improvement project, the next step is simple.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
            <button
              onClick={() => setOpenEstimate(true)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ef4444] px-8 py-4 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(239,68,68,0.35)] transition hover:scale-[1.02]"
            >
              Get Free Estimate
              <FaArrowRight />
            </button>

            <button
              onClick={() => setPage("faq")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-8 py-4 text-sm font-semibold text-white/90"
            >
              View FAQ
            </button>
          </div>
        </div>
      </GlassCard>
    </ScrollReveal>
  </motion.div>
);
  };

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#0b0b0c] via-[#111113] to-[#1a1a1c] text-white">
      <div ref={pageTopRef} />
      <div className="fixed inset-0 -z-30 bg-[radial-gradient(1200px_700px_at_50%_-5%,rgba(239,68,68,0.18),transparent_55%),linear-gradient(to_bottom,rgba(8,10,18,1),rgba(5,7,12,1))]" />
      <div className="fixed inset-0 -z-20 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:120px_120px] opacity-[0.08]" />

      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/25 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-6">
          <button
            onClick={() => setPage("home")}
            className="flex items-center gap-3 text-left"
          >
            <img
              src={logo}
              alt="HTX Home Solutions"
              className="h-12 w-12 rounded-2xl border border-white/10 object-cover"
            />
            <div>
              <p className="text-sm font-semibold tracking-wide text-white">
                HTX Home Solutions
              </p>
              <p className="text-xs text-white/50">
                Full-Service Home Contractor
              </p>
            </div>
          </button>

          <div className="hidden items-center gap-2 md:flex">
            {nav.map((item) => (
              <button
                key={item.key}
                onClick={() => setPage(item.key)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                  page === item.key
                    ? "bg-white text-black shadow-[0_10px_25px_rgba(255,255,255,0.18)]"
                    : "text-white/70 hover:bg-white/[0.05] hover:text-white"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="hidden md:block">
            <button
              onClick={() => setOpenEstimate(true)}
              className="rounded-full bg-[#ef4444] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(239,68,68,0.35)]"
            >
              Free Estimate
            </button>
          </div>

          <button
            onClick={() => setMobileMenu((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.05] text-white md:hidden"
          >
            <FaBars />
          </button>
        </div>

        <AnimatePresence>
          {mobileMenu ? (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-white/10 md:hidden"
            >
              <div className="mx-auto flex max-w-7xl flex-col gap-2 px-5 py-4">
                {nav.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => {
                      setPage(item.key);
                      setMobileMenu(false);
                    }}
                    className={`rounded-2xl px-4 py-3 text-left text-sm font-medium ${
                      page === item.key
                        ? "bg-white text-black"
                        : "bg-white/[0.05] text-white/75"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}

                <button
                  onClick={() => {
                    setOpenEstimate(true);
                    setMobileMenu(false);
                  }}
                  className="mt-2 rounded-2xl bg-[#ef4444] px-4 py-3 text-sm font-semibold text-white"
                >
                  Free Estimate
                </button>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </header>

      <main className="mx-auto max-w-7xl px-5 pb-20 pt-10 sm:px-6 sm:pt-14">
        <AnimatePresence mode="wait">{renderPage()}</AnimatePresence>
      </main>

  <footer className="border-t border-white/10 bg-black/18 backdrop-blur-2xl">
  <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 py-6 text-center text-xs text-white/50 sm:flex-row sm:px-6">
    <div className="flex flex-col items-center gap-2 sm:items-start">
      <div>© {new Date().getFullYear()} HTX Home Solutions</div>

      <div className="flex items-center gap-3 text-white/45">
        <span>Houston, Texas</span>
        <span>•</span>
        <span>Residential & Commercial Services</span>
      </div>
    </div>

    <div className="flex items-center gap-4 text-white/60">
      <a
        href="/privacy.html"
        className="underline underline-offset-4 hover:text-white"
      >
        Privacy Policy
      </a>

      <a
        href="/terms.html"
        className="underline underline-offset-4 hover:text-white"
      >
        Terms of Service
      </a>
    </div>
  </div>
</footer>

      <AnimatePresence>
        {openEstimate ? (
          <EstimateModal open={openEstimate} onClose={() => setOpenEstimate(false)} />
        ) : null}
      </AnimatePresence>
    </div>
  );
}